import { Component, NO_ERRORS_SCHEMA, OnInit } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { FlagComponent } from './flag/flag.component';
import { TranslateModule } from '@ngx-translate/core';
import { ProductContextService } from '../core/product-context.service';
import { GeoLocationService } from '../geo-location/geo-location.service';
import { LandingPageConfigService } from '../landing-page-config/landing-page-config.service';
import { LandingPageConfigDto, StaticLinkBox } from '../landing-page-config/landing-page-config.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, filter } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-product-info',
  standalone: true,
  imports: [MatChipsModule, CommonModule, FlagComponent, TranslateModule, RouterModule],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './product-info.component.html',
  styleUrl: './product-info.component.scss'
})
export class ProductInfoComponent implements OnInit {
  public productInfo: any = [];
  public staticLinkBoxes: StaticLinkBox[] = [];
  public language: string = 'en';
  public salesOrganization = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productContextService: ProductContextService,
    private geoLocationService: GeoLocationService,
    private landingPageConfigService: LandingPageConfigService,
  ) {
    // Re-init when navigating between serial numbers (component is reused by Angular router).
    // takeUntilDestroyed() ensures cleanup on destroy — prevents subscription leak.
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe(() => this.init());
  }

  public ngOnInit() {
    this.init();
  }

  private static readonly IMAGE_MAP: Record<string, string> = {
    REGISTER: 'assets/img/teaser-register.jpg',
    VIBOOKS:  'assets/img/teaser-vibooks.jpg',
    HOMEPAGE: 'assets/img/teaser-homepage.jpg',
  };

  getImage(type: string): string {
    return ProductInfoComponent.IMAGE_MAP[type] ?? 'assets/img/noimage.png';
  }

  private static readonly CTA_KEY_MAP: Record<string, string> = {
    REGISTER: 'link.cta.register',
    VIBOOKS:  'link.cta.vibooks',
    HOMEPAGE: 'link.cta.homepage',
  };

  getCTAKey(type: string): string {
    return ProductInfoComponent.CTA_KEY_MAP[type] ?? 'link.more';
  }

  private static readonly PRODUCT_IMAGE_MAP: Array<[string, string]> = [
    ['vitocal 250-a',   'assets/img/products/vitocal-250-a.png'],
    ['vitocal 200-s',   'assets/img/products/vitocal-200-s.png'],
    ['vitocal 252-a',   'assets/img/products/vitocal-252-a.png'],
    ['vitocal 300-a',   'assets/img/products/vitocal-300-a.png'],
    ['vitocal 350-a',   'assets/img/products/vitocal-350-a.png'],
    ['vitocal 333-g',   'assets/img/products/vitocal-333-g.png'],
    ['vitocal 150-a',   'assets/img/products/vitocal-150-a.png'],
    ['vitocal 111-s',   'assets/img/products/vitocal-111-s.png'],
    ['vitocal 151-a',   'assets/img/products/vitocal-150-a.png'],
    ['vitocal 200-a',   'assets/img/products/vitocal-250-a.png'],
    ['vitocal 222-s',   'assets/img/products/vitocal-222-s.png'],
    ['vitocal 222-g',   'assets/img/products/vitocal-333-g.png'],
    ['vitovolt 300',    'assets/img/products/vitovolt-300.png'],
    ['vitodens 200-w',  'assets/img/products/vitodens-200-w.png'],
  ];

  getProductImage(imageUrl: string | null, productName: string | null): string {
    if (imageUrl) return imageUrl;
    if (productName) {
      const normalized = productName.toLowerCase();
      const match = ProductInfoComponent.PRODUCT_IMAGE_MAP.find(([key]) => normalized.includes(key));
      if (match) return match[1];
    }
    return 'assets/img/product-placeholder.png';
  }

  getCountryName(countryCode: string): string {
    if (!countryCode) return '';
    try {
      const displayNames = new Intl.DisplayNames([this.language], { type: 'region' });
      return displayNames.of(countryCode.toUpperCase()) ?? countryCode;
    } catch {
      return countryCode;
    }
  }

  buildLink(link: string, serial: string): string {
    if (serial) {
      return link.replace('{serialnumber}', serial);
    }
    return link.replace(/[?&][^?&#]*\{serialnumber\}/, '').replace(/\{serialnumber\}/g, '');
  }

  private init() {
    this.productInfo = this.route.snapshot.data['productInformation'];
    this.salesOrganization = this.route.snapshot.params['salesOrganization'];
    this.language = this.route.snapshot.params['language'];

    // The not-sold route (/product-information/not-sold/:id) has no salesOrganization/language
    // params and doesn't run the landingPageConfig resolver at all (see app.routes.ts), so we can
    // tell it apart from a sold-product route where the resolver simply failed.
    const hasConfigResolver = 'landingPageConfig' in this.route.snapshot.data;
    const config = this.route.snapshot.data['landingPageConfig'];

    if (config) {
      this.applyConfig(config);
    } else if (hasConfigResolver) {
      // Sold product, but the config resolver failed - preserve existing behavior (no tiles).
      this.staticLinkBoxes = [];
    } else {
      // Not-sold product: derive tiles/locale from a best-effort visitor location instead.
      this.staticLinkBoxes = [];
      this.resolveFallbackConfig();
    }
  }

  private applyConfig(config: LandingPageConfigDto) {
    this.staticLinkBoxes = config.staticLinkBoxes;
    this.productContextService.setAvailableLanguages(config.availableLanguages);
    this.productContextService.setLocale(config.locale);
  }

  /**
   * The backend usually can't resolve country/salesOrganization for a serial number that isn't
   * sold to a customer (e.g. showroom units without a sales organization). In that case, fall
   * back to a best-effort guess of the visitor's location via their IP address
   * (GET /visitor-location) to still show the same locale-aware tiles (register/ViBooks/homepage)
   * as a regular product page. The browser's own locale supplies the language, and - if the IP
   * lookup fails too - also the country as a last resort.
   */
  private resolveFallbackConfig() {
    const language = this.browserLanguage();

    if (this.productInfo?.country) {
      this.loadFallbackTilesForCountry(this.productInfo.country, language);
      return;
    }

    this.geoLocationService.getVisitorLocation(this.route.snapshot.queryParams['ip']).pipe(
      catchError(() => of({ country: null }))
    ).subscribe(({ country }) => {
      const resolvedCountry = country || this.browserCountry();
      if (resolvedCountry) {
        this.loadFallbackTilesForCountry(resolvedCountry, language);
      } else {
        // No real country signal at all (geo-IP failed, browser locale has no region,
        // e.g. navigator.language === 'en') - don't invent one, just show whitelabel tiles.
        this.loadWhitelabelTiles(language);
      }
    });
  }

  private loadFallbackTilesForCountry(country: string, language: string) {
    this.landingPageConfigService.getConfigForCountry(country, language).pipe(
      catchError(() => of(null))
    ).subscribe(config => {
      if (config) {
        this.productInfo.country = config.country;
        this.language = config.language;
        this.applyConfig(config);
      } else {
        // Country couldn't be mapped to any sales organization (e.g. no geo-IP match and an
        // unresolvable browser locale) - fall back to the generic whitelabel tiles so the page
        // never ends up with zero tiles.
        this.loadWhitelabelTiles(language);
      }
    });
  }

  // Same convention as WhitelabelComponent: no real country known, so only distinguish
  // language, not an invented country.
  private loadWhitelabelTiles(language: string) {
    this.landingPageConfigService.getWhitelabelConfig().pipe(
      catchError(() => of(null))
    ).subscribe(config => {
      if (config) {
        this.staticLinkBoxes = config.staticLinkBoxes;
        this.productContextService.setAvailableLanguages(config.availableLanguages);
      } else {
        this.staticLinkBoxes = [];
      }
      this.language = language;
      this.productContextService.setLocale(language.startsWith('de') ? 'de-DE' : 'en-DE');
    });
  }

  private browserLanguage(): string {
    return (navigator.language || 'en-US').split('-')[0].toLowerCase() || 'en';
  }

  private browserCountry(): string | null {
    const region = (navigator.language || '').split('-')[1];
    return region ? region.toUpperCase() : null;
  }
}
